import React, { useEffect, useRef, useState } from 'react';
import { AlertCircle, KeyRound, Mail } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { parseOtpError } from '../../lib/otpErrors';

// Connexion par code à usage unique reçu par email.
//
// C'est le canal de secours des boîtes d'entreprise : les protections de
// messagerie (Safe Links, Proofpoint) pré-cliquent les liens à usage unique des
// mails Supabase et les brûlent avant l'utilisateur — un code, lui, ne se
// clique pas. Prérequis côté Supabase : le template « Magic Link » doit
// contenir le jeton {{ .Token }}, sinon le mail ne transporte qu'un lien.

/** Cooldown Supabase entre deux envois à la même adresse. */
const RESEND_COOLDOWN_SECONDS = 60;

// La longueur du code est un réglage Supabase (Auth → Email OTP Length), pas une
// constante : ce projet est configuré sur 8 alors que la valeur par défaut est 6.
// Un champ figé à 6 rendait le code reçu impossible à saisir — constaté au test
// du 2026-08-20. On accepte donc toute la plage autorisée par Supabase.
const MIN_CODE_LENGTH = 6;
const MAX_CODE_LENGTH = 10;

// Le code que l'utilisateur a sous les yeux peut venir de trois mails
// différents, et GoTrue les vérifie sous trois types distincts :
//   - 'email'  : « Magic Link », envoyé à un compte déjà confirmé ;
//   - 'signup' : « Confirm signup ». Piège observé le 2026-08-20 — sur un compte
//     encore non confirmé, ce qui est le cas de tout invité, signInWithOtp
//     déclenche `user_confirmation_requested` et donc CE template, pas le magic
//     link ;
//   - 'invite' : « Invite user », le code du mail d'invitation lui-même.
// On essaie les trois plutôt que d'exiger de l'utilisateur qu'il sache lequel il
// a reçu — ils ont tous la même forme.
const OTP_TYPES = ['email', 'signup', 'invite'] as const;

interface EmailCodeFormProps {
  /** Adresse pré-remplie. */
  initialEmail?: string;
  /** Adresse imposée (invitation : le code part sur l'adresse invitée). */
  lockEmail?: boolean;
  /**
   * Autorise la création du compte à la volée. Réservé à la page d'invitation,
   * où l'adresse est imposée par l'invitation elle-même : l'invité n'a pas
   * encore de compte, un code doit pouvoir lui en ouvrir un. Sur /login, ce
   * serait un formulaire d'inscription déguisé.
   */
  allowCreateUser?: boolean;
  /**
   * Un mail vient de partir (inscription) : on ouvre directement la saisie du
   * code plutôt que de demander d'en réclamer un — le deuxième envoi se
   * heurterait de toute façon au cooldown de 60 s de Supabase.
   */
  startAtVerify?: boolean;
  /** Session établie — à la charge de l'appelant de rediriger. */
  onSuccess: () => void | Promise<void>;
  /** Retour au mode précédent (mot de passe, choix des fournisseurs…). */
  onCancel?: () => void;
  cancelLabel?: string;
}

export default function EmailCodeForm({
  initialEmail = '',
  lockEmail = false,
  allowCreateUser = false,
  startAtVerify = false,
  onSuccess,
  onCancel,
  cancelLabel = 'Revenir au mot de passe',
}: EmailCodeFormProps) {
  const [step, setStep] = useState<'request' | 'verify'>(startAtVerify ? 'verify' : 'request');
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(startAtVerify ? RESEND_COOLDOWN_SECONDS : 0);
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown(s => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  useEffect(() => {
    if (step === 'verify') codeInputRef.current?.focus();
  }, [step]);

  const sendCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const address = email.trim();
    if (!address || busy || cooldown > 0) return;

    setBusy(true);
    setError('');
    setNotice('');
    const { error: sendError } = await supabase.auth.signInWithOtp({
      email: address,
      options: { shouldCreateUser: allowCreateUser },
    });
    setBusy(false);

    const info = parseOtpError(sendError);
    if (info) {
      setError(info.message);
      if (info.code === 'RATE_LIMITED') {
        setCooldown(info.retryAfterSeconds ?? RESEND_COOLDOWN_SECONDS);
        // Le mail précédent est bien parti : l'utilisateur peut saisir son code.
        setStep('verify');
      }
      return;
    }

    setCooldown(RESEND_COOLDOWN_SECONDS);
    setNotice(`Code envoyé à ${address}. Il est valable une heure.`);
    setStep('verify');
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    const token = code.trim();
    if (token.length < MIN_CODE_LENGTH || busy) return;

    setBusy(true);
    setError('');

    let lastError = null;
    for (const type of OTP_TYPES) {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: email.trim(),
        token,
        type,
      });
      if (!verifyError) {
        await onSuccess();
        setBusy(false);
        return;
      }
      lastError = verifyError;
      // Un 429 ne se rejoue pas sous un autre type : c'est l'adresse qui est
      // en cooldown, pas le code.
      if (parseOtpError(verifyError)!.code === 'RATE_LIMITED') break;
    }

    setBusy(false);
    setCode('');
    setError(parseOtpError(lastError)!.message);
  };

  return (
    <div className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 bg-red-950/50 border border-red-800 rounded-lg px-4 py-3 text-red-400 text-sm">
          <AlertCircle size={16} className="shrink-0" />
          {error}
        </div>
      )}
      {!error && notice && (
        <div className="bg-slate-800/60 border border-slate-700 rounded-lg px-4 py-3 text-slate-300 text-sm">
          {notice}
        </div>
      )}

      {step === 'request' ? (
        <form onSubmit={sendCode} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Email</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail size={16} className="text-slate-500" />
              </div>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                readOnly={lockEmail}
                placeholder="vous@exemple.com"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={busy || cooldown > 0}
            className="w-full bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
          >
            {busy ? 'Envoi…' : cooldown > 0 ? `Nouveau code dans ${cooldown} s` : 'Recevoir un code par email'}
          </button>

          <p className="text-xs text-slate-500 text-center">
            Un code, pas un lien : les messageries d'entreprise ouvrent les liens
            avant vous et les rendent inutilisables.
          </p>
        </form>
      ) : (
        <form onSubmit={verifyCode} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Code reçu à {email.trim()}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <KeyRound size={16} className="text-slate-500" />
              </div>
              <input
                ref={codeInputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, MAX_CODE_LENGTH))}
                required
                placeholder="••••••"
                className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-lg tracking-[0.4em] font-mono text-white placeholder-slate-600 focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={busy || code.length < MIN_CODE_LENGTH}
            className="w-full bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors text-sm"
          >
            {busy ? 'Vérification…' : 'Se connecter'}
          </button>

          <div className="flex items-center justify-between text-xs">
            <button
              type="button"
              onClick={() => sendCode()}
              disabled={busy || cooldown > 0}
              className="text-amber-500 hover:text-amber-400 disabled:text-slate-600 disabled:cursor-not-allowed font-medium transition-colors"
            >
              {cooldown > 0 ? `Renvoyer dans ${cooldown} s` : 'Renvoyer un code'}
            </button>
            {!lockEmail && (
              <button
                type="button"
                onClick={() => { setStep('request'); setCode(''); setError(''); setNotice(''); }}
                className="text-slate-400 hover:text-slate-300 transition-colors"
              >
                Changer d'adresse
              </button>
            )}
          </div>
        </form>
      )}

      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="w-full text-center text-sm text-slate-400 hover:text-slate-300 transition-colors"
        >
          {cancelLabel}
        </button>
      )}
    </div>
  );
}
